import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Grid, Box, Typography,
  FormControl, InputLabel, Select, MenuItem, Chip, Alert, Switch, FormControlLabel, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface SystemSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  settings: any;
  onSave: (settingsData: any) => void;
}

const SystemSettingsDialog: React.FC<SystemSettingsDialogProps> = ({ open, onClose, settings, onSave }) => {
  const [formData, setFormData] = useState({
    siteName: settings?.siteName || 'Prowess PR Platform',
    siteDescription: settings?.siteDescription || 'AI-powered press release distribution',
    maintenanceMode: settings?.maintenanceMode || false,
    debugMode: settings?.debugMode || false,
    emailNotifications: settings?.emailNotifications || true,
    autoBackup: settings?.autoBackup || true,
    backupFrequency: settings?.backupFrequency || 'daily',
    maxFileSize: settings?.maxFileSize || '10MB',
    allowedFileTypes: settings?.allowedFileTypes || ['pdf', 'doc', 'docx', 'txt'],
    apiRateLimit: settings?.apiRateLimit || 1000,
    sessionTimeout: settings?.sessionTimeout || 30,
    securityLevel: settings?.securityLevel || 'high',
    twoFactorAuth: settings?.twoFactorAuth || true,
    ipWhitelist: settings?.ipWhitelist || [],
    blockedDomains: settings?.blockedDomains || ['gmail.com', 'yahoo.com', 'hotmail.com']
  });

  const [newIp, setNewIp] = useState('');
  const [newDomain, setNewDomain] = useState('');

  const handleAddIp = () => {
    if (newIp && !formData.ipWhitelist.includes(newIp)) {
      setFormData({
        ...formData,
        ipWhitelist: [...formData.ipWhitelist, newIp]
      });
      setNewIp('');
    }
  };

  const handleRemoveIp = (ip: string) => {
    setFormData({
      ...formData,
      ipWhitelist: formData.ipWhitelist.filter((i: string) => i !== ip)
    });
  };

  const handleAddDomain = () => {
    if (newDomain && !formData.blockedDomains.includes(newDomain)) {
      setFormData({
        ...formData,
        blockedDomains: [...formData.blockedDomains, newDomain]
      });
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setFormData({
      ...formData,
      blockedDomains: formData.blockedDomains.filter((d: string) => d !== domain)
    });
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ backgroundColor: '#0927eb', color: 'white' }}>
        System Settings
      </DialogTitle>
      <DialogContent style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '20px' }}>
        <Grid container spacing={2}>
          {/* General Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginBottom: '15px', color: '#0927eb' }}>
              General Settings
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Site Name"
              value={formData.siteName}
              onChange={(e) => setFormData({...formData, siteName: e.target.value})}
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Site Description"
              value={formData.siteDescription}
              onChange={(e) => setFormData({...formData, siteDescription: e.target.value})}
              margin="normal"
            />
          </Grid>

          {/* System Controls */}
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '15px', color: '#0927eb' }}>
              System Controls
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.maintenanceMode}
                  onChange={(e) => setFormData({...formData, maintenanceMode: e.target.checked})}
                />
              }
              label="Maintenance Mode"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.debugMode}
                  onChange={(e) => setFormData({...formData, debugMode: e.target.checked})}
                />
              }
              label="Debug Mode"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.emailNotifications}
                  onChange={(e) => setFormData({...formData, emailNotifications: e.target.checked})}
                />
              }
              label="Email Notifications"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoBackup}
                  onChange={(e) => setFormData({...formData, autoBackup: e.target.checked})}
                />
              }
              label="Auto Backup"
            />
          </Grid>

          {/* Security Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '15px', color: '#0927eb' }}>
              Security Settings
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Security Level</InputLabel>
              <Select
                value={formData.securityLevel}
                onChange={(e) => setFormData({...formData, securityLevel: e.target.value})}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="maximum">Maximum</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.twoFactorAuth}
                  onChange={(e) => setFormData({...formData, twoFactorAuth: e.target.checked})}
                />
              }
              label="Two-Factor Authentication"
            />
          </Grid>

          {/* IP Whitelist */}
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '15px', color: '#0927eb' }}>
              IP Whitelist
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <TextField
                label="Add IP Address"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                style={{ marginRight: '10px', flex: 1 }}
                placeholder="e.g., 192.168.1.100"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddIp}
                style={{ backgroundColor: '#0927eb' }}
              >
                Add IP
              </Button>
            </Box>
            <List style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
              {formData.ipWhitelist.map((ip: string) => (
                <ListItem key={ip}>
                  <ListItemText primary={ip} />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleRemoveIp(ip)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Blocked Domains */}
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '15px', color: '#0927eb' }}>
              Blocked Email Domains
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <TextField
                label="Add Domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                style={{ marginRight: '10px', flex: 1 }}
                placeholder="e.g., gmail.com"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddDomain}
                style={{ backgroundColor: '#0927eb' }}
              >
                Add Domain
              </Button>
            </Box>
            <List style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
              {formData.blockedDomains.map((domain: string) => (
                <ListItem key={domain}>
                  <ListItemText primary={domain} />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleRemoveDomain(domain)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '20px' }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} style={{ backgroundColor: '#0927eb' }}>
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemSettingsDialog;
